<script setup lang="ts">
// Single-value gauge with optional coloured threshold bands. `value`
// is a marker (resolved upstream — always arrives as a number here) or
// a literal scalar. Threshold bands are sorted ascending and clamped
// within `[min, max]`.
//
// Round-3: clickable wrapper. The gauge has no native ECharts hit
// target, so the outer container fires `onClick` with the resolved
// status — the threshold-band's `label` if labels are configured,
// otherwise one of `"below" | "within" | "above"` based on whether
// the value falls under the lowest threshold, between thresholds, or
// above the highest.
//
// Template note: `<v-chart>` is the sole root of this component's
// template — DO NOT precede it with HTML comments inside `<template>`.
// A leading template comment counts as a second root node and
// disables Vue 3's scoped-CSS cascade from the parent `InsightChart`
// onto the `.chart` root, which strips `display: block` + the card
// chrome and leaves ECharts measuring `offsetWidth === 0` (falling
// back to its 100px internal default — gauge renders tiny in the
// top-left of an empty card). Keep design notes in this script
// comment instead.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { GaugeChartSpec, GaugeThreshold } from "../sbi/protocol";
import { formatValue } from "../format.ts";
import { CHART_COLORS, FONT_STACK_MONO } from "../theme.ts";
import {
  chartChrome,
  chartLabelColor,
  chartTextColor,
  resolveCssVar,
} from "./echarts-base";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = defineProps<{
  spec: GaugeChartSpec;
  formats?: Record<string, string>;
}>();

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const min = computed(() => props.spec.min ?? 0);
const max = computed(() => props.spec.max ?? 100);

// `value` is either a scalar literal (number) or a resolved marker
// (which the host patches into the `data` slot — the runtime maps
// `value: marker` onto `value: <resolved>` before render).
const numericValue = computed<number>(() => {
  const v = props.spec.value;
  if (typeof v === "number") return v;
  if (Array.isArray(v) && v.length > 0) {
    const first = (v[0] ?? {}) as Record<string, unknown>;
    const candidates = Object.values(first);
    const found = candidates.find((c) => typeof c === "number");
    if (typeof found === "number") return found;
    if (candidates[0] !== undefined) return Number(candidates[0]);
  }
  return NaN;
});

const sortedThresholds = computed<GaugeThreshold[]>(() => {
  const t = props.spec.thresholds ?? [];
  return [...t].sort((a, b) => a.value - b.value);
});

const status = computed(() => {
  const v = numericValue.value;
  if (!Number.isFinite(v)) return "unknown";
  const ts = sortedThresholds.value;
  if (ts.length === 0) {
    if (v < min.value) return "below";
    if (v > max.value) return "above";
    return "within";
  }
  for (const t of ts) {
    if (v <= t.value) return t.label ?? "within";
  }
  return ts[ts.length - 1]?.label ?? "above";
});

/**
 * Token-first colour pickers — try the embedder's declared
 * `--lucent-gauge-*` value, fall back to the canvas-safe theme
 * mirror. Reading the tokens preserves the embedder restyle path and
 * satisfies the CSS-token-contract test.
 */
const gaugeColour = {
  low: () =>
    resolveCssVar("var(--lucent-gauge-threshold-low)") ??
    CHART_COLORS.gaugeThresholdLow,
  mid: () =>
    resolveCssVar("var(--lucent-gauge-threshold-mid)") ??
    CHART_COLORS.gaugeThresholdMid,
  high: () =>
    resolveCssVar("var(--lucent-gauge-threshold-high)") ??
    CHART_COLORS.gaugeThresholdHigh,
  needle: () =>
    resolveCssVar("var(--lucent-gauge-needle)") ?? CHART_COLORS.gaugeNeedle,
  track: () =>
    resolveCssVar("var(--lucent-gauge-track)") ?? CHART_COLORS.gaugeTrack,
};

const option = computed(() => {
  const ts = sortedThresholds.value;
  const span = max.value - min.value || 1;
  // Default ramp for the three-band gauge case (low / mid / high).
  // Author-supplied `t.color` (a `var(--*)` reference per validator)
  // resolves at render time; falls back to the gauge-token default
  // for the band index when missing or unresolved. Tokens preserve
  // embedder restyle; theme.ts mirrors keep canvas painting safe.
  const defaultByIndex = (i: number, n: number): string => {
    if (n === 1) return gaugeColour.mid();
    if (i === 0) return gaugeColour.low();
    if (i === n - 1) return gaugeColour.high();
    return gaugeColour.mid();
  };
  const colorRamp: [number, string][] = ts.length
    ? ts.map((t, i) => {
        const stop = (t.value - min.value) / span;
        const colour = resolveCssVar(t.color) ?? defaultByIndex(i, ts.length);
        return [Math.min(Math.max(stop, 0), 1), colour];
      })
    : [[1, gaugeColour.track()]];
  if (ts.length && (colorRamp[colorRamp.length - 1]?.[0] ?? 0) < 1) {
    colorRamp.push([1, gaugeColour.high()]);
  }
  // Dial furniture scales with the radius (ECharts' default 75% of the
  // shorter side). k = 1 at the reference size — 250px tall in a 680px
  // PDF slot — so print and wide embeds are unchanged; in a narrow
  // column the 18px band no longer eats a quarter of the dial and the
  // tick numbers stop colliding with the readout.
  const w = ctx.value.width;
  const radius = (Math.min(w > 0 ? w : Infinity, height.value) / 2) * 0.75;
  const k = w > 0 ? Math.min(1, Math.max(0.55, radius / 94)) : 1;
  const band = Math.round(18 * k);
  return {
    ...chartChrome(ctx.value),
    series: [
      {
        type: "gauge" as const,
        min: min.value,
        max: max.value,
        progress: { show: true, width: band },
        axisLine: {
          lineStyle: {
            width: band,
            color: colorRamp,
          },
        },
        splitLine: { length: Math.round(10 * k) },
        axisTick: { length: Math.round(6 * k) },
        // Tick numbers around the dial — without a font override they
        // render in ECharts' default sans, which clashes with the mono
        // central readout below.
        axisLabel: {
          show: radius >= 50,
          color: chartLabelColor(),
          fontFamily: FONT_STACK_MONO,
          fontSize: Math.max(9, Math.round(10 * k)),
          distance: Math.round(15 * k),
        },
        pointer: { itemStyle: { color: gaugeColour.needle() } },
        // Central numeric readout. Mono + tabular alignment for free —
        // matches the KPI tile readouts elsewhere in the dashboard.
        detail: {
          formatter: props.spec.format
            ? (v: number) => formatValue(v, props.spec.format!) as string
            : undefined,
          color: chartTextColor(),
          fontFamily: FONT_STACK_MONO,
          fontSize: Math.round(18 * Math.max(k, 0.75)),
          fontWeight: 600,
          offsetCenter: [0, "70%"],
        },
        data: [{ value: numericValue.value }],
      },
    ],
  };
});

const height = computed(() =>
  resolveChartHeight("gauge", props.spec.height),
);

function onClick() {
  if (!props.spec.id) return;
  emit("click-target", props.spec.id, {
    value: numericValue.value,
    min: min.value,
    max: max.value,
    status: status.value,
  });
}
</script>

<template>
  <v-chart
    class="chart gauge-clickable"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    role="button"
    tabindex="0"
    @click="onClick"
    @keydown.enter="onClick"
    @keydown.space.prevent="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
.gauge-clickable {
  cursor: pointer;
}
.gauge-clickable:focus-visible {
  outline: 2px solid var(--lucent-chart-focus, currentColor);
  outline-offset: 2px;
}
</style>
